import moment from 'moment';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { EVALUATION_DEFAULTS } from '../../constants/chatAssistant.constants';
import {
  Card,
  Tag,
  Empty,
  Collapse,
  Typography,
  Button,
  notification,
  Space,
  Switch,
  DatePicker,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import ExampleQuestionsModal from './ExampleQuestionsModal';
import { t } from '../../i18n/i18n';
import { getSubskillsByIds } from '../../API/services/screeningService';
import './EvaluationDetails.scss';

const { Panel } = Collapse;
const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const EvaluationDetails = ({
  evaluationData,
  isEvaluationReady,
  onCreateEvaluation,
  onUpdateEvaluation,
  isCreatingEvaluation,
  currentDraftId,
  currentDraftData,
}) => {
  const { subskills, customTestGroup, evaluationDraft, evaluationMetadata } = evaluationData;
  const difficultyConfig = customTestGroup?.difficultyConfig;
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [enrichedSubskills, setEnrichedSubskills] = useState([]);

  useEffect(() => {
    const fetchSubskills = async () => {
      if (!subskills?.length) {
        setEnrichedSubskills([]);
        return;
      }

      const isIdFormat = subskills.every((s) => typeof s === 'string' && /^[a-f\d]{24}$/i.test(s));
      if (!isIdFormat) {
        setEnrichedSubskills(subskills);
        return;
      }

      const fetchedSubskills = await getSubskillsByIds(subskills);
      setEnrichedSubskills(fetchedSubskills.length ? fetchedSubskills : subskills);
    };

    fetchSubskills();
  }, [subskills]);

  const handleShowExamplesForSkill = (skill) => {
    setSelectedSkill(skill);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedSkill(null);
  };

  const handleSwitchChange = useCallback(
    async (field, value) => {
      if (currentDraftData?.evaluationOpened) {
        notification.warning({
          message: t('WARNING'),
          description: t('CHAT_EVALUATION_ALREADY_OPENED'),
        });
        return;
      }

      if (onUpdateEvaluation) {
        const updates = { ...evaluationData, [field]: value };
        const draftUpdate = { [field]: value };

        if (field === 'proctoringEnabled' && !value) {
          updates.proctoringAIEnabled = false;
          draftUpdate.proctoringAIEnabled = false;
        }

        if (field === 'emptyDates') {
          if (value) {
            updates.startDate = null;
            updates.endDate = null;
            draftUpdate.startDate = null;
            draftUpdate.endDate = null;
            draftUpdate.emptyDates = true;
          } else {
            updates.startDate = moment().toISOString();
            updates.endDate = moment().add(EVALUATION_DEFAULTS.DAYS_TO_ADD, 'days').toISOString();
            draftUpdate.startDate = updates.startDate;
            draftUpdate.endDate = updates.endDate;
            draftUpdate.emptyDates = false;
          }
        }

        onUpdateEvaluation(updates, currentDraftId);
      }
    },
    [evaluationData, onUpdateEvaluation, currentDraftData, currentDraftId]
  );

  const renderSubskills = useMemo(
    () => () => {
      if (!enrichedSubskills || enrichedSubskills.length === 0) {
        return <Empty description={t('CHAT_NO_EVALUATION_DATA')} />;
      }

      return (
        <Space direction="vertical" style={{ width: '100%' }}>
          {enrichedSubskills.map((skill, index) => {
            const skillName = typeof skill === 'string' ? skill : skill?.name || t('CHAT_NO_NAME');
            const skillData = typeof skill === 'string' ? { name: skill, id: skill } : skill;
            return (
              <Card
                key={index}
                size="small"
                bordered={false}
                style={{ backgroundColor: '#f8f9fa' }}
              >
                <div className="d-flex align-items-start justify-content-between">
                  <Tag
                    color="blue"
                    className="text-truncate"
                    style={{ maxWidth: 'calc(100% - 120px)' }}
                  >
                    {skillName}
                  </Tag>
                  <Button
                    size="small"
                    type="text"
                    icon={<BulbOutlined />}
                    onClick={() => handleShowExamplesForSkill(skillData)}
                    className="d-flex align-items-center flex-shrink-0"
                    style={{ borderRadius: 0 }}
                  >
                    {t('CHAT_VIEW_EXAMPLES')}
                  </Button>
                </div>
              </Card>
            );
          })}
        </Space>
      );
    },
    [enrichedSubskills]
  );

  const renderDifficultyConfig = useMemo(
    () => () => {
      if (!difficultyConfig && !evaluationMetadata) {
        return <Empty description={t('CHAT_NO_DIFFICULTY_CONFIG')} />;
      }

      const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
          case 'beginner':
            return 'green';
          case 'intermediate':
            return 'orange';
          case 'advanced':
            return 'red';
          default:
            return 'blue';
        }
      };

      const getDifficultyLabel = (difficulty) => {
        switch (difficulty) {
          case 'beginner':
            return t('CHAT_DIFFICULTY_BEGINNER');
          case 'intermediate':
            return t('CHAT_DIFFICULTY_INTERMEDIATE');
          case 'advanced':
            return t('CHAT_DIFFICULTY_ADVANCED');
          default:
            return difficulty;
        }
      };

      const displayData = evaluationMetadata || difficultyConfig;

      if (!displayData) {
        return <Empty description={t('CHAT_NO_DIFFICULTY_CONFIG')} />;
      }

      const totalQuestions = displayData.totalQuestions || displayData.total_questions;
      const estimatedTime = displayData.estimatedTime || displayData.estimated_time;
      const difficulty = displayData.difficulty;

      return (
        <Card size="small" bordered={false} style={{ backgroundColor: '#f8f9fa' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {totalQuestions && (
              <div className="d-flex justify-content-between align-items-center">
                <Text type="secondary">{t('CHAT_TOTAL_QUESTIONS')}:</Text>
                <Text strong>{totalQuestions}</Text>
              </div>
            )}
            {estimatedTime && (
              <div className="d-flex justify-content-between align-items-center">
                <Text type="secondary">{t('CHAT_ESTIMATED_TIME')}:</Text>
                <Text strong>
                  {estimatedTime} {t('CHAT_EVALUATION_MINUTES')}
                </Text>
              </div>
            )}
            {difficulty && (
              <div className="d-flex justify-content-between align-items-center">
                <Text type="secondary">{t('CHAT_DIFFICULTY')}:</Text>
                <Tag color={getDifficultyColor(difficulty)}>{getDifficultyLabel(difficulty)}</Tag>
              </div>
            )}
          </Space>
        </Card>
      );
    },
    [difficultyConfig, evaluationMetadata]
  );

  const hasEvaluationData =
    enrichedSubskills.length > 0 || difficultyConfig || evaluationMetadata || customTestGroup;

  if (!hasEvaluationData) {
    return (
      <div className="evaluation-details empty-state">
        <Empty
          description={
            <div>
              <Title level={5}>{t('CHAT_EVALUATION_DETAILS_TITLE')}</Title>
              <Text type="secondary">{t('CHAT_EVALUATION_DETAILS_EMPTY')}</Text>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className="evaluation-details">
      <Card title={t('CHAT_EVALUATION_DETAILS_TITLE')} size="small">
        <div className="evaluation-sections">
          <div className="section">
            <div className="section-header">
              <Title level={5} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BulbOutlined style={{ fontSize: '16px' }} />
                <span>
                  {t('CHAT_EVALUATION_SKILLS')} ({enrichedSubskills.length})
                </span>
              </Title>
            </div>
            {renderSubskills()}
          </div>

          {(difficultyConfig || evaluationMetadata) && (
            <div className="section">
              <div className="section-header">
                <Title level={5} className="d-flex align-items-center">
                  <ClockCircleOutlined className="mr-2" style={{ fontSize: '16px' }} />
                  <span>{t('CHAT_EVALUATION_DIFFICULTY')}</span>
                </Title>
              </div>
              {renderDifficultyConfig()}
            </div>
          )}

          {hasEvaluationData && (
            <div className="section">
              <Collapse ghost defaultActiveKey={[]}>
                <Panel
                  header={
                    <Title level={5} className="d-flex align-items-center">
                      <SettingOutlined className="mr-2" style={{ fontSize: '16px' }} />
                      <span>{t('CHAT_ADVANCED_CONFIG')}</span>
                    </Title>
                  }
                  key="advanced-config"
                >
                  <Space direction="vertical" className="w-100" size="middle">
                    <Card size="small" bordered={false} style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <Space>
                          <Text>{t('CREATE_EVALUATION_QUICK_CONFIG_ENABLE_PROCTORING')}</Text>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                {t('CREATE_EVALUATION_ENABLE_PROCTORING_DESCRIPTION')}
                              </Tooltip>
                            }
                          >
                            <InfoCircleOutlined
                              style={{
                                color: '#8c8c8c',
                                fontSize: '14px',
                                cursor: 'pointer',
                                marginRight: '8px',
                              }}
                            />
                          </OverlayTrigger>
                        </Space>
                        <Switch
                          checked={evaluationData.proctoringEnabled || false}
                          onChange={(checked) => handleSwitchChange('proctoringEnabled', checked)}
                          disabled={currentDraftData?.evaluationOpened}
                        />
                      </div>

                      {evaluationData.proctoringEnabled && (
                        <div className="mt-3 pl-4 d-flex justify-content-between align-items-center">
                          <Space>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                              {t('CREATE_EVALUATION_FOURTH_STEP_PROCTORING_AI_DESCRIPTION')}
                            </Text>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip>
                                  {t('CREATE_EVALUATION_FOURTH_STEP_PROCTORING_AI_DESCRIPTION')}
                                </Tooltip>
                              }
                            >
                              <InfoCircleOutlined
                                style={{
                                  color: '#8c8c8c',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  marginRight: '8px',
                                }}
                              />
                            </OverlayTrigger>
                          </Space>
                          <Switch
                            size="small"
                            checked={evaluationData.proctoringAIEnabled || false}
                            onChange={(checked) =>
                              handleSwitchChange('proctoringAIEnabled', checked)
                            }
                            disabled={currentDraftData?.evaluationOpened}
                          />
                        </div>
                      )}
                    </Card>

                    <Card size="small" bordered={false} style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <Space>
                          <Text>{t('CREATE_EVALUATION_QUICK_CONFIG_ALLOW_DOWNLOADS')}</Text>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                {t('CREATE_EVALUATION_ALLOW_DOWNLOAD_CERTIFICATES_DESCRIPTION')}
                              </Tooltip>
                            }
                          >
                            <InfoCircleOutlined
                              style={{
                                color: '#8c8c8c',
                                fontSize: '14px',
                                cursor: 'pointer',
                                marginRight: '8px',
                              }}
                            />
                          </OverlayTrigger>
                        </Space>
                        <Switch
                          checked={evaluationData.allowCertificateDownload !== false}
                          onChange={(checked) =>
                            handleSwitchChange('allowCertificateDownload', checked)
                          }
                          disabled={currentDraftData?.evaluationOpened}
                        />
                      </div>
                    </Card>

                    <Card size="small" bordered={false} style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <Space>
                          <Text>{t('ENABLE_COMPLETE_PROFILE_DROPDOWN_TITLE')}</Text>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                {t('ENABLE_COMPLETE_PROFILE_CERTIFICATES_DESCRIPTION')}
                              </Tooltip>
                            }
                          >
                            <InfoCircleOutlined
                              style={{
                                color: '#8c8c8c',
                                fontSize: '14px',
                                cursor: 'pointer',
                                marginRight: '8px',
                              }}
                            />
                          </OverlayTrigger>
                        </Space>
                        <Switch
                          checked={evaluationData.requireCompletedProfile || false}
                          onChange={(checked) =>
                            handleSwitchChange('requireCompletedProfile', checked)
                          }
                          disabled={currentDraftData?.evaluationOpened}
                        />
                      </div>
                    </Card>

                    <Card size="small" bordered={false} style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <Space>
                          <Text>{t('CREATE_EVALUATION_PLAN_DATES')}</Text>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>{t('CREATE_EVALUATION_PLAN_DATES_DETAIL_EXPANDED')}</Tooltip>
                            }
                          >
                            <InfoCircleOutlined
                              style={{
                                color: '#8c8c8c',
                                fontSize: '14px',
                                cursor: 'pointer',
                                marginRight: '8px',
                              }}
                            />
                          </OverlayTrigger>
                        </Space>
                        <Switch
                          checked={!evaluationData.emptyDates}
                          onChange={(checked) => handleSwitchChange('emptyDates', !checked)}
                          disabled={currentDraftData?.evaluationOpened}
                        />
                      </div>

                      {!evaluationData.emptyDates && (
                        <div className="mt-3 pl-4">
                          <RangePicker
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY HH:mm"
                            showTime={{ format: 'HH:mm' }}
                            placeholder={[t('CHAT_START_DATE'), t('CHAT_END_DATE')]}
                            value={[
                              evaluationData.startDate ? moment(evaluationData.startDate) : null,
                              evaluationData.endDate ? moment(evaluationData.endDate) : null,
                            ]}
                            onChange={(dates) => {
                              if (onUpdateEvaluation && dates && dates[0] && dates[1]) {
                                onUpdateEvaluation({
                                  ...evaluationData,
                                  startDate: dates[0].toISOString(),
                                  endDate: dates[1].toISOString(),
                                }, currentDraftId);
                              }
                            }}
                            disabledDate={(current) => current && current < moment().startOf('day')}
                            disabled={currentDraftData?.evaluationOpened}
                          />
                        </div>
                      )}
                    </Card>

                    <Card size="small" bordered={false} style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <Space>
                          <Text>{t('CREATE_EVALUATION_WITH_NOT_ACCOUNT_TITLE')}</Text>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                {t('CREATE_EVALUATION_WITH_NOT_ACCOUNT_DESCRIPTION')}
                              </Tooltip>
                            }
                          >
                            <InfoCircleOutlined
                              style={{
                                color: '#8c8c8c',
                                fontSize: '14px',
                                cursor: 'pointer',
                                marginRight: '8px',
                              }}
                            />
                          </OverlayTrigger>
                        </Space>
                        <Switch
                          checked={evaluationData.allowAnonymousAccess || false}
                          onChange={(checked) =>
                            handleSwitchChange('allowAnonymousAccess', checked)
                          }
                          disabled={currentDraftData?.evaluationOpened}
                        />
                      </div>
                    </Card>

                    <Card size="small" bordered={false} style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <Space>
                          <Text>
                            {t('CREATE_EVALUATION_QUICK_CONFIG_ENABLE_EXCLUSIVE_TEMP_USERS')}
                          </Text>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                {t('CREATE_EVALUATION_ENABLE_EXCLUSIVE_TEMP_USERS_DESCRIPTION')}
                              </Tooltip>
                            }
                          >
                            <InfoCircleOutlined
                              style={{
                                color: '#8c8c8c',
                                fontSize: '14px',
                                cursor: 'pointer',
                                marginRight: '8px',
                              }}
                            />
                          </OverlayTrigger>
                        </Space>
                        <Switch
                          checked={evaluationData.exclusiveAccess || false}
                          onChange={(checked) => handleSwitchChange('exclusiveAccess', checked)}
                          disabled={currentDraftData?.evaluationOpened}
                        />
                      </div>
                    </Card>

                    <Card size="small" bordered={false} style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <Space>
                          <Text>{t('REMEMBER_TEST')}</Text>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{t('REMEMBER_TEST_DETAILS')}</Tooltip>}
                          >
                            <InfoCircleOutlined
                              style={{
                                color: '#8c8c8c',
                                fontSize: '14px',
                                cursor: 'pointer',
                                marginRight: '8px',
                              }}
                            />
                          </OverlayTrigger>
                        </Space>
                        <Switch
                          checked={evaluationData.enableReminders !== false}
                          onChange={(checked) => handleSwitchChange('enableReminders', checked)}
                          disabled={currentDraftData?.evaluationOpened}
                        />
                      </div>
                    </Card>
                  </Space>
                </Panel>
              </Collapse>
            </div>
          )}
        </div>

        {(evaluationData.evaluationDraft?.customTestGroup ||
          evaluationData.customTestGroup ||
          isEvaluationReady) && (
          <div className="mt-3">
            <Button
              type="primary"
              size="large"
              block
              disabled={
                !isEvaluationReady || isCreatingEvaluation || currentDraftData?.evaluationOpened
              }
              loading={isCreatingEvaluation}
              onClick={() => {
                if (currentDraftData?.evaluationOpened) {
                  notification.warning({
                    message: t('WARNING'),
                    description: t('CHAT_EVALUATION_ALREADY_OPENED'),
                  });
                  return;
                }

                const evaluationToCreate = {
                  name:
                    evaluationData.evaluationMetadata?.name ||
                    evaluationData.name ||
                    t('CHAT_TECHNICAL_EVALUATION'),
                  language: evaluationData.language || 'es',
                  duration:
                    evaluationData.evaluationMetadata?.estimatedTime ||
                    evaluationData.evaluationMetadata?.estimated_time ||
                    evaluationData.duration ||
                    60,
                  purpose: evaluationData.purpose || 'OTHER',
                  customTestGroup: evaluationData.customTestGroup,
                  startDate: new Date().toISOString(),
                  endDate: new Date(
                    Date.now() +
                      EVALUATION_DEFAULTS.DAYS_TO_ADD * EVALUATION_DEFAULTS.MILLISECONDS_PER_DAY
                  ).toISOString(),
                };
                onCreateEvaluation(evaluationToCreate, currentDraftId);
              }}
              style={{
                height: '48px',
                fontSize: '16px',
                fontWeight: '600',
                backgroundColor: isEvaluationReady ? 'var(--primary-color)' : undefined,
                borderColor: isEvaluationReady ? 'var(--primary-color)' : undefined,
                borderRadius: 0,
              }}
            >
              {isCreatingEvaluation ? (
                t('CHAT_CREATING_EVALUATION') || 'Creando evaluación...'
              ) : currentDraftData?.evaluationOpened ? (
                <span className="d-flex align-items-center justify-content-center">
                  <CheckCircleOutlined style={{ fontSize: '18px' }} />
                  <span className="ml-2">{t('CHAT_EVALUATION_ALREADY_OPENED')}</span>
                </span>
              ) : isEvaluationReady ? (
                <span className="d-flex align-items-center justify-content-center">
                  <CheckCircleOutlined style={{ fontSize: '18px' }} />
                  <span className="ml-2">🎉 {t('CHAT_OPEN_EVALUATION')}</span>
                </span>
              ) : (
                <span className="d-flex align-items-center justify-content-center">
                  <ClockCircleOutlined style={{ fontSize: '18px' }} />
                  <span className="ml-2">{t('CHAT_PREPARING_EVALUATION')}</span>
                </span>
              )}
            </Button>
            {isEvaluationReady && (
              <Text
                type="secondary"
                className="d-block text-center mt-2"
                style={{ fontSize: '12px' }}
              >
                {t('CHAT_EVALUATION_READY_MESSAGE')}
              </Text>
            )}
          </div>
        )}
      </Card>

      {modalVisible && selectedSkill && (
        <ExampleQuestionsModal
          visible={modalVisible}
          onClose={handleCloseModal}
          skill={selectedSkill}
          evaluationContext={{
            ...evaluationData,
            role: evaluationDraft?.name || t('CHAT_TECHNICAL_EVALUATION_LOWERCASE'),
            language: evaluationDraft?.language || 'es',
            purpose: evaluationDraft?.purpose || 'RECRUITMENT',
          }}
        />
      )}
    </div>
  );
};

export default EvaluationDetails;
